import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Overlay, Popover } from 'react-bootstrap';

const PremiumDatePicker = ({ value, onChange, placeholder = "dd/mm/yyyy", className = "" }) => {
    const [show, setShow] = useState(false);
    const target = useRef(null);
    const [viewDate, setViewDate] = useState(new Date());
    const [inputValue, setInputValue] = useState("");
    const [error, setError] = useState("");

    // Handle selected date for calendar logic
    const selectedDate = useMemo(() => {
        if (!value) return null;
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    }, [value]);

    // Update inputValue when value prop changes
    useEffect(() => {
        if (!value) {
            setInputValue("");
            return;
        }
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
            setInputValue(d.toLocaleDateString('en-GB')); // dd/mm/yyyy
        }
    }, [value]);

    useEffect(() => {
        if (selectedDate) setViewDate(selectedDate);
    }, [selectedDate]);

    const handleInputChange = (e) => {
        let val = e.target.value;
        const isDeleting = e.nativeEvent.inputType === 'deleteContentBackward';
        
        // Remove non-digits
        let digits = val.replace(/\D/g, '');
        let formatted = digits;
        
        // Auto-format as dd/mm/yyyy
        if (digits.length > 2) {
            formatted = digits.slice(0, 2) + '/' + digits.slice(2);
        }
        if (digits.length > 4) {
            formatted = formatted.slice(0, 5) + '/' + digits.slice(4, 8);
        }
        
        formatted = formatted.slice(0, 10);
        setInputValue(formatted);
        setError(""); // Clear error while typing

        // Try to parse dd/mm/yyyy
        const parts = formatted.split('/');
        if (parts.length === 3 && parts[2].length === 4) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            
            // Basic validity check
            const date = new Date(year, month, day);
            const isValid = !isNaN(date.getTime()) && 
                          date.getDate() === day && 
                          date.getMonth() === month && 
                          year > 1900;

            if (isValid) {
                const yyyy = date.getFullYear();
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                onChange(`${yyyy}-${mm}-${dd}`);
                setError("");
            } else {
                setError("Invalid date");
                onChange(""); // Clear parent value on invalid input
            }
        } else if (formatted === "") {
            onChange("");
            setError("");
        }
    };

    const handleDateSelect = (date) => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        onChange(`${yyyy}-${mm}-${dd}`);
        setError("");
        setShow(false);
    };

    const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const generateDays = () => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const totalDays = daysInMonth(year, month);
        const startDay = firstDayOfMonth(year, month);
        
        const days = [];
        // Pad previous month days to align week
        for (let i = 0; i < startDay; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
        }
        // Current month days
        for (let d = 1; d <= totalDays; d++) {
            const date = new Date(year, month, d);
            const isToday = new Date().toDateString() === date.toDateString();
            const isSelected = selectedDate?.toDateString() === date.toDateString();
            
            days.push(
                <div 
                    key={d} 
                    className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleDateSelect(date)}
                >
                    {d}
                </div>
            );
        }
        return days;
    };

    const nextMonth = (e) => {
        e.stopPropagation();
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    const prevMonth = (e) => {
        e.stopPropagation();
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    return (
        <div className={`premium-datepicker-container d-flex align-items-center ${className}`} ref={target}>
            <div 
                className="datepicker-input-wrapper d-flex align-items-center border rounded px-2"
                style={{ height: '31px', background: '#fff', cursor: 'text', flexGrow: 1, position: 'relative' }}
            >
                <i className="bi bi-calendar3 me-2 text-muted" style={{ fontSize: '14px', cursor: 'pointer' }} onClick={() => setShow(!show)}></i>
                <input
                    type="text"
                    className="border-0 bg-transparent p-0 shadow-none"
                    style={{ fontSize: '13px', color: '#333', outline: 'none', width: '100%' }}
                    value={inputValue}
                    placeholder={placeholder}
                    onChange={handleInputChange}
                    onFocus={() => setShow(true)}
                />
                {error && (
                    <div className="position-absolute top-100 start-0 text-danger mt-1" style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>
                        {error}
                    </div>
                )}
            </div>
            {(value || inputValue) && (
                <i className="bi bi-x-circle-fill ms-2 text-dark clear-icon" onClick={(e) => { e.stopPropagation(); setInputValue(""); onChange(""); setError(""); }}></i>
            )}

            <Overlay target={target.current} show={show} placement="bottom-start" rootClose onHide={() => setShow(false)}>
                {(props) => (
                    <Popover id="datepicker-popover" {...props} className="premium-calendar-popover shadow-lg border-0" style={{ ...props.style, zIndex: 2000 }}>
                        <Popover.Body className="p-0">
                            <div className="calendar-card p-3 shadow-sm border rounded-4 bg-white" style={{ minWidth: '260px' }}>
                                <div className="calendar-header d-flex justify-content-between align-items-center mb-3">
                                    <button className="btn btn-link p-1 text-dark" onClick={prevMonth}><i className="bi bi-chevron-left"></i></button>
                                    <div className="fw-bold month-year-title text-dark">
                                        {viewDate.toLocaleString('default', { month: 'long' })} {viewDate.getFullYear()}
                                    </div>
                                    <button className="btn btn-link p-1 text-dark" onClick={nextMonth}><i className="bi bi-chevron-right"></i></button>
                                </div>
                                <div className="calendar-weekdays d-grid mb-1 text-center text-muted fw-bold" style={{ gridTemplateColumns: 'repeat(7, 1fr)', fontSize: '11px' }}>
                                    <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
                                </div>
                                <div className="calendar-grid d-grid text-center" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
                                    {generateDays()}
                                </div>
                                <div className="calendar-footer mt-3 pt-3 border-top d-flex justify-content-between">
                                    <button className="btn btn-sm btn-outline-primary px-3 rounded-pill" onClick={() => handleDateSelect(new Date())} style={{ fontSize: '11px' }}>Today</button>
                                    <button className="btn btn-sm btn-link text-muted p-0" onClick={() => { onChange(""); setShow(false); }} style={{ fontSize: '11px', textDecoration: 'none' }}>Clear</button>
                                </div>
                            </div>
                        </Popover.Body>
                    </Popover>
                )}
            </Overlay>

            <style>{`
                .premium-calendar-popover {
                    border-radius: 16px !important;
                    background: transparent !important;
                    border: none !important;
                }
                .calendar-card {
                    font-family: inherit;
                }
                .month-year-title {
                    font-size: 14px;
                }
                .calendar-day {
                    aspect-ratio: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    font-size: 13px;
                    border-radius: 8px;
                    margin: 1px;
                    transition: all 0.2s;
                    color: #333;
                }
                .calendar-day:hover:not(.empty) {
                    background-color: #f1f5f9;
                }
                .calendar-day.today {
                    color: #2563eb;
                    font-weight: bold;
                    background-color: #eff6ff;
                }
                .calendar-day.selected {
                    background-color: #2563eb !important;
                    color: #fff !important;
                    font-weight: bold;
                }
                .calendar-day.empty {
                    cursor: default;
                }
                .clear-icon {
                    cursor: pointer;
                    font-size: 14px;
                    transition: all 0.2s;
                    color: #000 !important;
                }
                .clear-icon:hover {
                    color: #dc3545 !important;
                    transform: scale(1.1);
                }
                .cursor-pointer {
                    cursor: pointer;
                }
            `}</style>
        </div>
    );
};

export default PremiumDatePicker;
